import React from "react";

const OpenSourceLayout = () => {
    return (
        <div className="bg-white">

            {/* ================= HERO ================= */}
            <section
                className="relative h-[520px] flex items-center text-white"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(10,15,30,.85), rgba(10,15,30,.85)), url(/images/opensource/hero.jpg)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="relative w-full px-6 md:px-16 lg:px-24 py-24 text-white text-left">
                    <span className="text-sm uppercase tracking-wide text-orange-400">
                        Services
                    </span>
                    <h1 className="text-4xl md:text-5xl font-bold mt-3">
                        TechSPR Open Source Hub
                    </h1>
                    <p className="max-w-2xl mt-4 text-lg text-gray-200">
                        Simplifying maintenance, security, and governance of enterprise
                        open-source components.
                    </p>
                    <button className="mt-6 bg-orange-500 hover:bg-orange-600 px-6 py-3 rounded-md font-semibold">
                        Sign Up for Open Source Hub
                    </button>
                </div>
            </section>

            {/* ================= INTRO ================= */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="text-gray-700 mb-4">
                            Enterprises today rely heavily on open-source technologies to
                            accelerate innovation. However, managing upgrades, security
                            patches, and compliance can become complex and resource-intensive.
                        </p>
                        <p className="text-gray-700">
                            Our Open Source Hub helps organizations modernize, secure, and
                            optimize their open-source ecosystem with confidence.
                        </p>
                    </div>
                    <div className="relative rounded-xl overflow-hidden shadow-xl">
                        <img
                            src="https://images.unsplash.com/photo-1553877522-43269d4ea984"
                            alt="Open Source Platform"
                        />
                    </div>
                </div>
            </section>

            {/* ================= HOW IT WORKS ================= */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-10">How It Works</h2>
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            "Register your organization",
                            "Analyze open-source inventory",
                            "Apply security & compliance checks",
                            "Deploy optimized upgrades",
                        ].map((step, i) => (
                            <div
                                key={i}
                                className="bg-white rounded-xl p-6 shadow-sm border"
                            >
                                <span className="text-orange-500 font-bold text-xl">
                                    0{i + 1}
                                </span>
                                <p className="mt-3 text-sm text-gray-700">{step}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= BENEFITS ================= */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-10">
                        Benefits of the Open Source Hub
                    </h2>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                text: "Minimized upgrade delays and risks",
                                img: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d",
                            },
                            {
                                text: "Enhanced security and compliance",
                                img: "https://images.unsplash.com/photo-1556155092-8707de31f9c4",
                            },
                            {
                                text: "Rigorous testing and validation",
                                img: "/images/opensource/bf1.jpg",
                            },
                            {
                                text: "Customized enterprise support",
                                img: "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70",
                            },
                            {
                                text: "Dedicated open-source experts",
                                img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e",
                            },
                            {
                                text: "Predictable upgrade roadmaps",
                                img: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4",
                            },
                        ].map((item, i) => (
                            <div
                                key={i}
                                className="rounded-xl overflow-hidden shadow-md bg-white"
                            >
                                <img
                                    src={item.img}
                                    alt={item.text}
                                    className="w-full h-48 object-cover"
                                />
                                <div className="p-5">
                                    <p className="font-semibold text-gray-800">
                                        {item.text}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            {/* ================= ADVANTAGE ================= */}
            <section className="py-20 bg-[#fde7d9]">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10">
                    {[
                        {
                            title: "Access Domain Expertise",
                            text: "Leverage deep open-source expertise across industries.",
                        },
                        {
                            title: "Project-Specific Recommendations",
                            text: "Tailored guidance aligned with your architecture.",
                        },
                        {
                            title: "Accelerated Delivery",
                            text: "Reduce operational friction with automation.",
                        },
                        {
                            title: "Focus on Innovation",
                            text: "Free teams to focus on high-value initiatives.",
                        },
                    ].map((a) => (
                        <div key={a.title}>
                            <h4 className="font-semibold mb-2">{a.title}</h4>
                            <p className="text-sm text-gray-700">{a.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ================= GLOBAL COMMUNITY ================= */}
            <section className="py-24 bg-[#020617] text-white">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <h2 className="text-3xl font-bold mb-8">
                        The Power of Our Global Community
                    </h2>
                    <div className="grid md:grid-cols-4 gap-8">
                        <div>
                            <h3 className="text-4xl font-bold text-orange-400">25,000+</h3>
                            <p className="text-sm text-gray-300 mt-2">
                                Developer community members
                            </p>
                        </div>
                        <div>
                            <h3 className="text-4xl font-bold text-orange-400">19</h3>
                            <p className="text-sm text-gray-300 mt-2">Countries served</p>
                        </div>
                        <div>
                            <h3 className="text-4xl font-bold text-orange-400">5M+</h3>
                            <p className="text-sm text-gray-300 mt-2">
                                Open-source project assets
                            </p>
                        </div>
                        <div>
                            <h3 className="text-4xl font-bold text-orange-400">35+</h3>
                            <p className="text-sm text-gray-300 mt-2">
                                Years of engineering excellence
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= LEADERSHIP ================= */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-6">
                        The Digital Engineering Leader
                    </h2>
                    <p className="max-w-3xl text-gray-700">
                        We help enterprises modernize open-source adoption with structured
                        governance, proactive security, and scalable delivery frameworks.
                    </p>
                    <button className="mt-6 bg-orange-500 text-white px-6 py-3 rounded-md">
                        Register Today
                    </button>
                </div>
            </section>

            {/* ================= CONTACT ================= */}
            {/* <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-8">Contact Us</h2>
                    <form className="grid md:grid-cols-2 gap-6">
                        <input className="border p-3 rounded" placeholder="First name" />
                        <input className="border p-3 rounded" placeholder="Last name" />
                        <input className="border p-3 rounded" placeholder="Work email" />
                        <input className="border p-3 rounded" placeholder="Company" />
                        <textarea
                            className="border p-3 rounded md:col-span-2"
                            rows="4"
                            placeholder="How can we help?"
                        />
                        <button className="bg-orange-500 text-white px-6 py-3 rounded-md font-semibold">
                            Submit
                        </button>
                    </form>
                </div>
            </section> */}

        </div>
    );
};

export default OpenSourceLayout;
